import type { NavigationGuard } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';
import { isAuthenticatedByRL } from '@/modules/auth/auth';
import { changingActiveSetFilters } from '@/modules/offline-assessment/api';
import { fetchSupplierInfo, type SupplierInfoResponse } from '@/modules/profile/api/supplier';
import { SUPPLIER_INFO_QUERY } from '@/modules/profile/api/queryKeys';
import { connectionCapabilities } from '@/modules/profile/composables/useConnectionCapabilities';

declare module 'vue-router' {
  interface RouteMeta {
    /** What a supplier profile tab needs the connection to allow before it opens. */
    connectionCapability?:
      | 'connectionAllowsDiscussions'
      | 'connectionAllowsActivity'
      | 'connectionAllowsCriticalSuppliers';
  }
}

/** Sends a profile tab the connection has turned off, such as a typed address, to Overview. */
export const requireConnectionCapability: NavigationGuard = async (to, from) => {
  const capability = to.meta.connectionCapability;
  if (!capability) return true;
  // Read before any await: the inject context lasts only until then.
  const queryClient = useQueryClient();
  const { id } = to.params;
  const overview = { name: 'suppliers-profile-overview', params: { id } };
  if (typeof id !== 'string') return overview;
  const connectionAllowsTab = (supplierInfo: SupplierInfoResponse) =>
    connectionCapabilities({
      orgID: id,
      isSupplier: true,
      supplierInfo,
      isChangingSet: queryClient.isMutating(changingActiveSetFilters(id)) > 0,
    })[capability];
  // Same tab, same supplier (e.g. another thread): a cached allow skips the wait.
  const sameTab = to.name === from.name && id === from.params.id;
  const cached = sameTab
    ? queryClient.getQueryData<SupplierInfoResponse>([SUPPLIER_INFO_QUERY, id])
    : undefined;
  if (cached && connectionAllowsTab(cached)) return true;
  // Leave logged-out users to requireAuth, which saves the address for after login.
  if (!(await isAuthenticatedByRL())) return true;
  try {
    return connectionAllowsTab(await fetchSupplierInfo(queryClient, id)) ? true : overview;
  } catch {
    return overview;
  }
};
